import React from "react";
import { usePaginationState } from "@components/shared/DataTable/Pagination";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";

import DataTableRenderer from "@components/shared/DataTable/DataTableRenderer";
import ApprovalTableActionButtons from "@components/shared/ApprovalTableActionButtons";
import ApprovalTag from "@components/shared/ApprovalTag";
import IndeterminateCheckbox from "@components/form/IndeterminateCheckbox";
import { getActionTimestamp } from "@utils/administrativeApproval";
import { useAllAdministrativeApprovals } from "@hooks/query/zenith/useAllAdministrativeApprovals";
import { AdministrativeApprovalNumericType } from "@services/administrativeApprovals";

const AdminTransferTable = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = usePaginationState();

  const {
    data: { administrativeApprovals, meta } = {
      administrativeApprovals: [],
      meta: {},
    },
    isLoading,
    isFetching,
  } = useAllAdministrativeApprovals({
    params: {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      types: [AdministrativeApprovalNumericType.AT],
    },
  });

  const { refetch: fetchAll, isFetching: isFetchingAll } =
    useAllAdministrativeApprovals({
      params: {
        types: [AdministrativeApprovalNumericType.AT],
        limit: -1,
      },
    });

  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ table, row }) => {
          const selectedIds = table
            .getSelectedRowModel()
            .rows.map(row => row.id);

          return (
            <IndeterminateCheckbox
              checked={selectedIds.includes(row.id)}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          );
        },
      },
      {
        header: t("extraction_right.name") as string,
        cell: ({ row }) => row.original.extractionRights[0]?.name,
      },
      {
        accessorKey: "extractionPoint.name",
        header: t("extraction_point.link.offtake_id") as string,
      },
      {
        header: t("extraction_point.link.entitlement_zone") as string,
        cell: ({ row }) =>
          row.original.extractionRights[0]?.level0Resource?.identifier,
      },
      {
        header: t("extraction_point.link.point_zone") as string,
        cell: ({ row }) => row.original.extractionPoint?.level0WRS?.identifier,
      },
      {
        accessorKey: "status",
        header: t("common.status") as string,
        cell: ({ row }) => {
          const { status, accountedAt } = row.original;
          return status ? (
            <ApprovalTag isAccounted={Boolean(accountedAt)} status={status} />
          ) : null;
        },
      },

      {
        header: t("common.actioned_at") as string,
        cell: ({ row }) => {
          return getActionTimestamp(row.original);
        },
      },
      {
        id: "actions",
        header: t("common.actions") as string,
        cell: ({ row }) => <ApprovalTableActionButtons row={row} />,
      },
    ],
    [t],
  );

  return (
    <DataTableRenderer
      name={t("approval.admin_transfer.title")}
      columns={columns}
      data={administrativeApprovals}
      pagination={pagination}
      onPaginationChange={setPagination}
      pageCount={meta.lastPage ?? -1}
      isLoading={isLoading}
      isFetching={isFetching || isFetchingAll}
      fetchAll={fetchAll}
      fetchAllDataKey="administrativeApprovals"
      stickyHeader
    />
  );
};

export default AdminTransferTable;
